/*
 * SEREIN motion — DESIGN.md § Motion is the source of truth.
 *
 * Two effects, nothing else:
 *   .serein-focus  Display headings blur into focus: opacity 0 → 1 and
 *                  blur(--motion-reveal-blur) → none, per LINE (never per
 *                  word). Explicit lines (<br>, or stacked block children)
 *                  stagger by --motion-reveal-stagger; a single line animates
 *                  as a whole.
 *   .serein-fade   Sections fade in: opacity 0 → 1, no movement.
 *
 * Both run once, the first time the element enters the viewport. Anything in
 * view when the page opens waits ~200ms so it never starts mid-paint.
 *
 * Safety: the hidden start states only exist under html.serein-motion, which
 * the inline script in theme.liquid adds before first paint — never with
 * prefers-reduced-motion, never in the theme editor. That script also arms a
 * 2s timeout that adds .serein-motion-off (everything visible). This file
 * cancels it only after its observers are running, so a failure anywhere
 * above leaves the safety net in place.
 *
 * Last resort (benchmark rules, October 2026: motion must always end
 * visible): 2500ms after this script starts, anything still without
 * .is-revealed gets it outright. If the observers or requestAnimationFrame
 * never fired, that is every element. If both are working, it is everything
 * in or above the viewport, so a heading the observer missed can never stay
 * hidden, while sections further down still fade in as they are scrolled to.
 */
(function () {
  var html = document.documentElement;
  if (!html.classList.contains('serein-motion')) return;
  // The safety net already fired (very slow load): leave everything visible
  // rather than hiding content the visitor may already be reading.
  if (html.classList.contains('serein-motion-off')) return;
  if (!('IntersectionObserver' in window)) {
    html.classList.add('serein-motion-off');
    return;
  }

  var FIRST_PAINT_DELAY = 200;
  var LAST_RESORT_DELAY = 2500;
  var initTime = window.performance ? performance.now() : Date.now();
  // Proof of life for the last resort: an observer delivers its first
  // callback as soon as it observes, and rAF runs on the next frame.
  var observersAlive = false;
  var rafAlive = false;
  requestAnimationFrame(function () { rafAlive = true; });
  var rootStyle = getComputedStyle(html);

  function ms(name, fallback) {
    var v = parseFloat(rootStyle.getPropertyValue(name));
    return isNaN(v) ? fallback : v;
  }

  var revealDuration = ms('--motion-reveal-duration', 1400);
  var revealStagger = ms('--motion-reveal-stagger', 80);
  var fadeDuration = ms('--motion-fade-duration', 800);

  function makeLine() {
    var span = document.createElement('span');
    span.className = 'serein-focus__line';
    return span;
  }

  function isBlock(el) {
    var d = getComputedStyle(el).display;
    return d === 'block' || d === 'flex' || d === 'grid' || d === 'list-item';
  }

  // Turn a heading's explicit lines into .serein-focus__line units. Returns
  // the number of lines (1 = animate the heading as a whole). Runs while the
  // heading is still hidden by CSS, so the restructure is never seen.
  function splitLines(el) {
    var nodes = Array.prototype.slice.call(el.childNodes);

    // Explicit <br> lines: wrap each run between breaks in its own block span
    // and drop the <br>, so the visual line breaks stay exactly where they were.
    if (nodes.some(function (n) { return n.nodeName === 'BR'; })) {
      var lines = [makeLine()];
      nodes.forEach(function (n) {
        if (n.nodeName === 'BR') {
          lines.push(makeLine());
        } else {
          lines[lines.length - 1].appendChild(n);
        }
      });
      lines = lines.filter(function (l) { return l.textContent.trim() !== ''; });
      if (lines.length < 2) {
        // A trailing or doubled <br> only: put the content back untouched.
        el.textContent = '';
        nodes.forEach(function (n) { el.appendChild(n); });
        return 1;
      }
      el.textContent = '';
      lines.forEach(function (l, i) {
        l.style.setProperty('--line-index', i);
        el.appendChild(l);
      });
      return lines.length;
    }

    // Stacked block children (several <p> in a quote, block-level spans):
    // each child is already its own line.
    var children = Array.prototype.slice.call(el.children);
    var looseText = nodes.some(function (n) {
      return n.nodeType === 3 && n.textContent.trim() !== '';
    });
    if (children.length > 1 && !looseText && children.every(isBlock)) {
      children.forEach(function (c, i) {
        c.classList.add('serein-focus__line');
        c.style.setProperty('--line-index', i);
      });
      return children.length;
    }

    return 1;
  }

  function reveal(el, duration) {
    // will-change only for the length of the animation: a permanent hint
    // keeps every heading on its own compositor layer for nothing.
    el.classList.add('is-animating');
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        el.classList.add('is-revealed');
      });
    });
    setTimeout(function () {
      el.classList.remove('is-animating');
    }, duration + 150);
  }

  function schedule(el, duration) {
    var now = window.performance ? performance.now() : Date.now();
    var wait = Math.max(0, initTime + FIRST_PAINT_DELAY - now);
    if (wait) {
      setTimeout(function () { reveal(el, duration); }, wait);
    } else {
      reveal(el, duration);
    }
  }

  // Headings: short boxes, so a real visibility threshold works.
  var headingObserver = new IntersectionObserver(function (entries) {
    observersAlive = true;
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      headingObserver.unobserve(entry.target);
      var lines = Number(entry.target.getAttribute('data-serein-lines')) || 1;
      schedule(entry.target, revealDuration + revealStagger * (lines - 1));
    });
  }, { threshold: 0.3 });

  // Sections: can be taller than the viewport, where a 30% threshold would
  // never be reached. Fire as the top edge clears the bottom tenth instead.
  var sectionObserver = new IntersectionObserver(function (entries) {
    observersAlive = true;
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      sectionObserver.unobserve(entry.target);
      schedule(entry.target, fadeDuration);
    });
  }, { threshold: 0, rootMargin: '0px 0px -10% 0px' });

  document.querySelectorAll('.serein-focus').forEach(function (el) {
    var lines = splitLines(el);
    el.setAttribute('data-serein-lines', lines);
    if (lines > 1) el.classList.add('is-split');
    headingObserver.observe(el);
  });

  document.querySelectorAll('.serein-fade').forEach(function (el) {
    sectionObserver.observe(el);
  });

  // Last resort, see the header. Adding .is-revealed directly (no rAF) so it
  // works even when frames are not being produced.
  setTimeout(function () {
    var broken = !observersAlive || !rafAlive;
    var pending = document.querySelectorAll('.serein-focus:not(.is-revealed), .serein-fade:not(.is-revealed)');
    Array.prototype.forEach.call(pending, function (el) {
      if (broken || el.getBoundingClientRect().top < window.innerHeight) {
        el.classList.add('is-revealed');
      }
    });
  }, LAST_RESORT_DELAY);

  // Observers are live: stand the 2s safety net down.
  clearTimeout(window.SereinMotionFallback);
})();
